import { memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { useViewportType } from '../../../utils/useViewportType';
import { SeasonPicker } from '../../../SeasonPicker/SeasonPicker';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';
import { useComponentPopup } from '../../ComponentPopup/useComponentPopup';

import type { SvpSeason } from '@vgtv/api-client/lib/svp_season';
import type { SvpSeasonSliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { FC } from 'react';

import * as styles from './SeasonsSlider.module.scss';

interface SeasonsSliderProps {
  componentConfig: SvpSeasonSliderComponent;
  pageComponentIndex: number;
}

const createSeasonSelectOptions = (
  seasons: SvpSeason[],
  replaceValue: string
) => {
  const regexp = new RegExp(`^${replaceValue} `, 'i');

  return seasons
    .map((season) => {
      return {
        value: season.seasonNumber,
        label: season.title.replace(regexp, ''),
        title: season.title,
      };
    })
    .reverse();
};

const getLatestSeasonNumber = (seasons: SvpSeason[]) => {
  return Math.max(...seasons.map((season) => season.seasonNumber));
};

const SeasonsSliderBase: FC<SeasonsSliderProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { seasons } = componentConfig;
  const { t } = useTranslation();

  const seasonOptions = useMemo(
    () => createSeasonSelectOptions(seasons, t('Season')),
    [seasons, t]
  );

  const [selectedSeasonNumber, setSelectedSeasonNumber] = useState(
    getLatestSeasonNumber(seasons)
  );

  const currentComponentConfig = useMemo(
    () =>
      componentConfig.components.find(
        (season) => season.seasonNumber === selectedSeasonNumber
      )!,
    [componentConfig, selectedSeasonNumber]
  );

  const { isFetched, visibleItemsCount } = useComponentDataFetch({
    componentConfig: currentComponentConfig,
    fetchAction: fetchSvpAssets,
  });

  const { setComponentPopupId } = useComponentPopup();

  const { isMobile } = useViewportType();

  const { id, options } = currentComponentConfig;

  const assets = useComponentAssets(id);

  const verticalThumbnail = options.useVerticalPosters;

  const headerButtonLabel = t('see all');

  const { trackAssetClick, trackHeaderButtonClick } = useAssetItemTracking({
    elementType: 'SeasonsSlider',
    header: currentComponentConfig.header,
    headerButtonLabel,
    pageComponentIndex,
  });

  const handleHeaderButtonClick = useCallback(() => {
    trackHeaderButtonClick();

    setComponentPopupId(currentComponentConfig.id);
  }, [trackHeaderButtonClick, setComponentPopupId, currentComponentConfig]);

  const items = useMemo(() => {
    if (!isFetched) {
      return [...Array(10)].map((_, index) => {
        return (
          <Placeholder
            key={index}
            type={
              verticalThumbnail
                ? placeholderTypes.assetWithVerticalThumbnail
                : placeholderTypes.assetVertical
            }
          />
        );
      });
    }

    if (assets.length) {
      return assets.map((asset, index) => {
        return (
          <AssetItem
            key={asset.id}
            asset={asset}
            componentConfig={componentConfig}
            aspectRatio={verticalThumbnail ? 'vertical' : 'horizontal'}
            onClick={() => trackAssetClick(asset, index)}
          />
        );
      });
    }

    return [
      <div style={{ visibility: 'hidden' }} key={0}>
        <Placeholder
          type={
            verticalThumbnail
              ? placeholderTypes.assetWithVerticalThumbnail
              : placeholderTypes.assetVertical
          }
        />
      </div>,
    ];
  }, [assets, componentConfig, isFetched, trackAssetClick, verticalThumbnail]);

  const showHeaderButton =
    assets.length > Math.ceil(visibleItemsCount as number);

  return (
    <Slider
      header={
        <SeasonPicker
          size={isMobile ? 'small' : 'large'}
          options={seasonOptions}
          value={selectedSeasonNumber}
          onChange={setSelectedSeasonNumber}
        />
      }
      disableHeaderContentClip
      headerButtonLabel={showHeaderButton ? headerButtonLabel : undefined}
      onHeaderButtonClick={
        showHeaderButton ? handleHeaderButtonClick : undefined
      }
      className={classNames(styles.slider, {
        [styles.vertical]: verticalThumbnail,
      })}
    >
      {items}
    </Slider>
  );
};

export const SeasonsSlider = memo(SeasonsSliderBase);
